import * as React from 'react'
import './style/domain-data-form.scss'
import { Flex } from 'antd'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import { classnames } from '@halo/_utils/classnames'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainService } from '@halo/services/domain.service'
import { DomainDto } from '@halo/dtos/domain.dto'
import { UserService } from '@halo/services/user.service'
import { AppState } from '@halo/stores/app.state'
import { DomainDetailsWell } from '@halo/components/domain-data-form/domain-details-well'
import { DomainServerWell } from '@halo/components/domain-data-form/domain-server-well'
import { DomainPromptWell } from '@halo/components/domain-data-form/domain-prompt-well'
import { SendIntegrationsWell } from '@halo/components/domain-data-form/domain-send-integrations-well'
import { DomainEcommWell } from '@halo/components/domain-data-form/domain-ecomm-well'
import { EngagementAttributionSettingsWell } from '@halo/components/domain-data-form/engagement-attribution-settings-well'
import { DomainGeographyWell } from '@halo/components/domain-data-form/domain-geography-well'
import { DomainPageAnalysisConfigWell } from '@halo/components/domain-data-form/domain-page-analysis-config-well'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'
import { hydrateDomainModel } from '@halo/_utils/models-hydration'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AppService } from '@halo/services'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { Domain } from '@pushly/models/lib/structs/domain'
import { DomainNotificationSettings } from '@pushly/aqe/lib/components'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { extractFriendlyErrorMessage, extractMinMaxFriendlyError } from '@pushly/aqe/lib/utils/exception'
import { DomainNotificationSettingsProps } from '@pushly/aqe/lib/components/domain-notification-settings/domain-notification-settings'
import { PlatformPreferencesWell } from '@pushly/aqe/lib/components/platform-preferences-well/platform-preferences-well'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { PlatformUserContext } from '@pushly/aqe/lib/providers/user-preferences-provider/internal'

interface IDomainDataForm {
    className?: string
    domainId?: number
}

export const DomainDataForm: React.FC<IDomainDataForm> = (props) => {
    const { className, domainId } = props

    const [loading, setLoading] = React.useState<boolean>(true)
    const [domain, setDomain] = React.useState<DomainDto | undefined>()
    const [pageAnalysisConfigs, setPageAnalysisConfigs] = React.useState<IPageAnalysisConfig[] | undefined>()

    const appState = Container.get(AppState)
    const appSvc = Container.get(AppService)
    const domainService = Container.get(DomainService)
    const pageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)

    const userContext = React.useContext(PlatformUserContext)

    const handleDomainUpdated = React.useCallback(async (updates: Partial<DomainDto> | Partial<Domain>) => {
        setDomain(
            (prevDomain) =>
                ({
                    ...prevDomain,
                    ...updates,
                }) as DomainDto,
        )
    }, [])

    const fetchPageAnalysisConfigs = React.useCallback(async () => {
        let configs: IPageAnalysisConfig[] = []

        if (appState.currentUser?.isInternalDevUser) {
            const res = await pageAnalysisConfigsService.fetchAll({
                query: { pagination: 0 },
            })

            if (res?.data) {
                configs = res.data
            }
        }

        return configs
    }, [appState.currentUser?.isInternalDevUser, pageAnalysisConfigsService])

    const fetchRequestedDomain = React.useCallback(
        async (id: number) => {
            const { data: requestedDomain } = await domainService.fetchById(id, {
                showLoadingScreen: false,
                cancellationKey: 'ddf-fetch',
            })

            return requestedDomain
        },
        [domainService],
    )

    const reloadState = React.useCallback(async () => {
        setLoading(true)

        const res = await Promise.all([fetchRequestedDomain(domainId!), fetchPageAnalysisConfigs()])

        const [fetchedDomain, fetchedPageAnalysisConfigs] = res

        setDomain(fetchedDomain)
        setPageAnalysisConfigs(fetchedPageAnalysisConfigs)
        setLoading(false)
    }, [domainId, fetchRequestedDomain, fetchPageAnalysisConfigs, userContext.preferences])

    React.useEffect(() => {
        reloadState()
    }, [reloadState])

    const onGeneralDomainEditSubmit = React.useCallback(
        async (builder: Parameters<DomainNotificationSettingsProps['onEditorSubmit']>[0]) => {
            let resOk = builder.isEmpty

            if (!builder.isEmpty) {
                try {
                    const values = Domain.parseJsonObject(builder.buildPartial(), { excludeDefaultValues: true })

                    const { ok, data } = await domainService.patchByDomainId(domainId!, values)
                    if (ok) {
                        await handleDomainUpdated(data)
                        resOk = true
                    }
                } catch (error) {
                    if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
                        if (
                            error.field === 'deliveryThrottleMaxSeconds' &&
                            (error.constraint === 'min' || error.constraint === 'max')
                        ) {
                            error = extractMinMaxFriendlyError(error, [0, 15], 'minutes')
                        } else {
                            error = extractFriendlyErrorMessage(error)
                        }
                    }
                    simpleNotification('error', error.message)
                }
            }

            return resOk
        },
        [domainId, domainService, handleDomainUpdated],
    )

    return (
        <div className={classnames('ddf', className)}>
            <Flex vertical gap={VERTICAL_WELL_GAP} className={classnames('ddf-wrapper')}>
                <DomainDetailsWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />

                <DomainServerWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />

                <SendIntegrationsWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />

                {domain && (
                    <DomainNotificationSettings
                        title="General Notification Settings"
                        loading={loading}
                        hideFooter={true}
                        showGeneralSettingsWell={true}
                        showNotificationAutomationWell={true}
                        showInternalEditorAccessFields={appState.abilityStore.can(
                            AbilityAction.UPDATE,
                            asCaslSubject(SubjectEntity.DOMAIN, {
                                ...domain,
                                statusId: domain?.status_id,
                            }),
                        )}
                        domain={hydrateDomainModel(domain)}
                        fetchCustomMacroFields={() => domainService.getCustomMacroFields(domain.id)}
                        getAppContainer={appSvc.getAppContainer}
                        onEditorSubmit={onGeneralDomainEditSubmit}
                        wellStyle="halo"
                    />
                )}

                <DomainPromptWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />

                <DomainEcommWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />

                {appState.currentUser?.isInternalUser && (
                    <EngagementAttributionSettingsWell
                        loading={loading}
                        domain={domain}
                        onDomainUpdated={handleDomainUpdated}
                    />
                )}

                {appState.currentUser?.isInternalUser && (
                    <DomainGeographyWell loading={loading} domain={domain} onDomainUpdated={handleDomainUpdated} />
                )}

                {appState.currentUser?.isInternalDevUser && (
                    <DomainPageAnalysisConfigWell
                        loading={loading}
                        domain={domain}
                        pageAnalysisConfigs={pageAnalysisConfigs}
                        onDomainUpdated={handleDomainUpdated}
                    />
                )}

                {domain && (
                    <PlatformPreferencesWell
                        loading={loading}
                        domainId={domain.id}
                        deliveryChannels={domain.activeChannels ?? [DeliveryChannel.WEB]}
                        preferences={userContext.preferences.state.bundle!}
                        onPreferencesUpdated={userContext.preferences.refresh}
                        userCanUpdatePlatformPreferences={appState.abilityStore.can(
                            AbilityAction.UPDATE,
                            asCaslSubject(SubjectEntity.DOMAIN, {
                                id: domainId!,
                                statusId: 1,
                            }),
                        )}
                    />
                )}
            </Flex>
        </div>
    )
}
